import { derivedDryCoolingForSed, waterScreenDataForSed } from '../economicContribution/sedDryCoolingScreen';
import type { DataSource, ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { WaterUseScreeningResult } from '../../types/economicContribution';
import { buildWaterScreeningView, citedWaterScreeningSources, renderWaterUseScreeningText } from '../economicContribution/sedEconomicEvidence';
import { isCurrentWaterUseScreeningResult } from '../economicContribution/waterUseScreening';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { effectivePhaseCapacity, effectivePower, effectivePowerMwRange } from '../projectIntent';
import { applyPhaseWaterDisclosure } from './phaseWaterDisclosure';

/** Compare frozen inputs only; a changed report scope needs a new screen. */
function matchesResolvedIntent(water: WaterUseScreeningResult, intent: ProjectIntent | null | undefined): boolean {
  if (!intent) return true; // Legacy callers have no report override to compare.
  const context = water.resolvedContext;
  const pueUnknown = isIntentFieldExplicitlyUnknown(intent, 'pueValue');
  if (pueUnknown && context?.pue.source === 'site-record') return false;
  const pue = !pueUnknown ? intent.pueValue ?? intent.pueTarget : undefined;
  if (pue != null && (context?.pue.low !== pue || context?.pue.high !== pue)) return false;
  const utilizationUnknown = isIntentFieldExplicitlyUnknown(intent, 'annualDemandUtilizationRangePct');
  if (utilizationUnknown && context?.utilization.source === 'site-record') return false;
  const utilization = !utilizationUnknown ? intent.annualDemandUtilizationRangePct : undefined;
  if (utilization && (context?.utilization.low !== utilization.low / 100
    || context?.utilization.high !== utilization.high / 100)) return false;
  const basisUnknown = isIntentFieldExplicitlyUnknown(intent, 'powerBasis');
  if (basisUnknown && water.loadRange.basisSource === 'site-record') return false;
  if (!basisUnknown && intent.powerBasis
    && water.loadRange.basis !== (intent.powerBasis === 'it-load' ? 'it' : 'facility')) return false;
  if (water.loadRange.phaseBasis === 'declared-project') {
    if (isIntentFieldExplicitlyUnknown(intent, 'powerRequirement')
      || isIntentFieldExplicitlyUnknown(intent, 'powerRequirementMW')) return false;
    if (intent.powerRequirement != null || intent.powerRequirementMW != null) {
      const range = effectivePowerMwRange(effectivePower(intent));
      if (!range || water.loadRange.low !== range.min || water.loadRange.high !== range.max) return false;
    }
  } else {
    // A selected phase screen follows its own capacity, never ultimate MW.
    const phase = intent.phases?.find(candidate => candidate.id === water.loadRange.phaseBasis);
    if (phase) {
      const range = effectivePhaseCapacity(phase).range;
      if (!range || water.loadRange.low !== range.min || water.loadRange.high !== range.max) return false;
    }
  }
  return true;
}

/** Materialize the complete frozen SED water screen as its own report section.
 * Existing narrative is retained and still checked for contradictory claims.
 * This is generation-time assembly, never a reader-time historical rewrite. */
export function applyWaterScreeningDisclosure(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  reportType = report.reportType ?? '',
  intent: ProjectIntent | null | undefined = report.projectIntentAtGen,
): SiteRagReport {
  return applyPhaseWaterDisclosure(applyPrimaryWaterScreeningDisclosure(report, sed, reportType, intent), sed, intent, reportType);
}

/** Seed generation/review citation slots through the same frozen projectors
 * used by final assembly. Only references escape this local context: owner
 * phase placeholders and generated disclosure prose never enter the report. */
export function waterScreeningReferencesForGeneration(
  references: readonly DataSource[],
  sed: SiteEvidenceDocument | null | undefined,
  reportType: string,
  siteId: string | undefined,
  reportCreatedAt: string | undefined,
  intent?: ProjectIntent | null,
): DataSource[] {
  const current = [...references];
  if (!sed || !siteId?.trim() || !reportCreatedAt?.trim()
    || !Number.isFinite(Date.parse(reportCreatedAt))
    || !['feasibility', 'water-resource', 'power-availability', 'environmental'].includes(reportType)) return current;
  const context = {
    siteId, sedId: sed.id, sedVersionUsed: sed.version, createdAt: reportCreatedAt,
    references: current, projectIntentAtGen: intent,
    ...(reportType === 'feasibility' ? { developmentPathway: (intent?.phases ?? []).map(phase => ({
      phase: phase.label || `Phase ${phase.sequence}`,
      title: `Phase ${phase.sequence}${phase.label ? ` (${phase.label})` : ''}`, description: '',
    })) } : {}),
    ...(reportType === 'environmental' ? { waterResources: {
      waterStressAnalysis: '', waterRightsAssessment: '', groundwaterSurfaceWater: '',
      thermalDischarge: '', stormwaterManagement: '',
    } } : {}),
  } as SiteRagReport;
  try {
    return applyWaterScreeningDisclosure(context, sed, reportType, intent).references ?? current;
  } catch {
    // Malformed frozen data stays with the existing generation quality gates.
    // A failed projector must not publish partially assembled citation slots.
    return current;
  }
}

function applyPrimaryWaterScreeningDisclosure(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  reportType = report.reportType ?? '',
  intent?: ProjectIntent | null,
): SiteRagReport {
  if (!['feasibility', 'water-resource', 'power-availability'].includes(reportType)) return report;
  const data = sed?.economicEvidence?.data;
  const water = data?.waterUseScreening;
  if (!sed || !data || water?.status !== 'calculated' || !water.scenarios.length
    || !isCurrentWaterUseScreeningResult(water)
    || !matchesResolvedIntent(water, intent)
    || (!!report.siteId && report.siteId !== water.siteId)) {
    // Neither a model-authored imitation nor a carried old projection can
    // masquerade as the deterministic section when its frozen input is absent.
    if (report.waterScreening === undefined && report.waterScreeningBasis === undefined
      && report.waterScreeningView === undefined) return report;
    const { waterScreening: _text, waterScreeningBasis: _basis, waterScreeningView: _view, ...without } = report;
    return without;
  }
  // Only the sources a scenario row cites; the saved text lists the same set.
  const derived = derivedDryCoolingForSed(sed);
  const projection = waterScreenDataForSed(sed, derived) ?? data;
  const paired = derived?.paired;
  const sources = citedWaterScreeningSources(projection);
  const references = [...(report.references ?? [])];
  const citationByUrl = new Map<string, number[]>();
  for (const source of sources) {
    const field = `economicEvidence.waterUseScreening.${source.id}`;
    const label = `${source.publisher}: ${source.title} (${source.locator})`;
    let index = references.findIndex(ref => ref.field === field && ref.url === source.url && ref.source === label
      && ref.qualification === 'estimated' && ref.sourceGrade === 'inference');
    if (index < 0) {
      const reference: DataSource = { field, source: label, url: source.url, retrievedAt: source.accessedAt,
        qualification: 'estimated', sourceGrade: 'inference' };
      index = references.push(reference) - 1;
    }
    const indices = citationByUrl.get(source.url) ?? [];
    if (!indices.includes(index + 1)) indices.push(index + 1);
    citationByUrl.set(source.url, indices);
  }
  // Keep the frozen source URLs and add ordinary report references; subsequent
  // reference renumbering updates this scalar alongside all other prose.
  const cited = { ...projection, waterScreeningSources: sources };
  const waterScreening = renderWaterUseScreeningText(cited, 'report')
    .replace(/<([^<>]+)>/g, (match, url: string) => `${match}${(citationByUrl.get(url) ?? []).map(index => ` [Ref ${index}]`).join('')}`);
  // The reader lays out the same formatted strings; it resolves each source's
  // reference by URL, so it carries no [Ref N] token of its own.
  const waterScreeningView = buildWaterScreeningView(cited);
  const waterScreeningBasis = {
    sedId: sed.id, sedVersion: sed.version, economicContentHash: data.contentHash,
    modelInputHash: water.modelInputHash, modelVersion: water.modelVersion,
    sourceCatalogVersion: water.sourceCatalogVersion,
    ...(paired ? { dryCoolingClimateSummaryHash: paired.summaryHash, dryCoolingTmyDatasetHash: paired.datasetHash } : {}),
  };
  const sectionOrder = report.sectionOrder ? [...report.sectionOrder] : undefined;
  if (sectionOrder && !sectionOrder.includes('waterScreening')) {
    const anchor = sectionOrder.findIndex(id => ['supplyAvailability', 'waterDuty', 'appendix', 'conclusion'].includes(id));
    sectionOrder.splice(anchor < 0 ? sectionOrder.length : anchor, 0, 'waterScreening');
  }
  return {
    ...report, references, waterScreening, waterScreeningBasis,
    ...(waterScreeningView ? { waterScreeningView } : {}),
    ...(sectionOrder ? { sectionOrder } : {}),
  };
}
